import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import interestFormConverter from "@/components/StudyCard/utils/interestFormConverter";
import { getFirestoreDB } from "@/firebase";
import { useStudiesHooksContextOptional, StudiesHooksProvider } from "./StudiesHooksContext";

export { StudiesHooksProvider };

/**
 * This hook keeps track of the studies saved by the user.
 * It listens to changes in the user's saved studies collection and updates the state accordingly.
 * Useful for indicating that a particular study has been saved by the user, and allowing the user to dynamically
 * save/unsave a study without manually triggering a re-fetch of the saved studies.
 *
 * @param uid
 * @returns list of saved study NCT IDs
 */
export default function useSavedStudies(uid: string | undefined): { savedStudies: string[]; isLoading: boolean } {
    const context = useStudiesHooksContextOptional();
    const shouldUseContext = Boolean(context?.uid && uid && context.uid === uid);

    const [savedStudies, setSavedStudies] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (shouldUseContext) {
            context?.ensureSavedStudies();
        }
    }, [context, shouldUseContext]);

    useEffect(() => {
        if (shouldUseContext) return undefined;

        if (!uid) {
            setSavedStudies([]);
            setIsLoading(false);
            return;
        }
        const db = getFirestoreDB();

        const savedStudiesRef = collection(db, "userProfile", uid, "savedStudies").withConverter(interestFormConverter);
        return onSnapshot(savedStudiesRef, (snapshot) => {
            const savedStudiesData = snapshot.docs.map((doc) => doc.data().nctID);
            console.log("Snapshot fired!");
            setSavedStudies(savedStudiesData);
            setIsLoading(false);
        });
    }, [uid, shouldUseContext]);

    if (shouldUseContext && context) {
        return { savedStudies: context.savedStudies, isLoading: context.savedStudiesLoading };
    }

    return { savedStudies, isLoading };
}

/**
 * Tracks the studies a user has enrolled in.
 * Listens to userProfile/{uid}/enrolledStudies for live updates.
 *
 * @param uid
 * @returns list of enrolled study NCT IDs
 */
export function useEnrolledStudies(uid: string | undefined) {
    const context = useStudiesHooksContextOptional();
    const normalizedUid = uid || undefined;
    const shouldUseContext = Boolean(context?.uid && normalizedUid && context.uid === normalizedUid);

    const [enrolledStudies, setEnrolledStudies] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (shouldUseContext) {
            context?.ensureEnrolledStudies();
        }
    }, [context, shouldUseContext]);

    useEffect(() => {
        if (shouldUseContext) return undefined;

        if (!normalizedUid) {
            setEnrolledStudies([]);
            setIsLoading(false);
            return undefined;
        }

        const db = getFirestoreDB();
        const enrolledStudiesRef = collection(db, "userProfile", normalizedUid, "enrolledStudies");
        return onSnapshot(enrolledStudiesRef, (snapshot) => {
            const enrolledStudiesData = snapshot.docs.map((doc) => {
                const data = doc.data() as { nctID?: string; nctId?: string };
                return data.nctID || data.nctId || doc.id;
            });
            setEnrolledStudies(enrolledStudiesData);
            setIsLoading(false);
        });
    }, [normalizedUid, shouldUseContext]);

    if (shouldUseContext && context) {
        return { enrolledStudies: context.enrolledStudies, isLoading: context.enrolledStudiesLoading };
    }

    return { enrolledStudies, isLoading };
}

type StudyRecommendation = {
    nctId: string;
    title: string;
    searchId: string;
    uid: string;
    runId: string;
};

export function useRecommendedStudies(uid: string | undefined) {
    const context = useStudiesHooksContextOptional();
    const shouldUseContext = Boolean(context?.uid && uid && context.uid === uid);

    const [recommendedStudies, setRecommendedStudies] = useState<StudyRecommendation[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (shouldUseContext) {
            context?.ensureRecommendedStudies();
        }
    }, [context, shouldUseContext]);

    useEffect(() => {
        if (shouldUseContext) return undefined;

        if (!uid) {
            setRecommendedStudies([]);
            setIsLoading(false);
            return;
        }
        const db = getFirestoreDB();

        const recommendedStudiesRef = collection(db, "userProfile", normalizedUid, "trialFinderRecommendations");
        const q = query(recommendedStudiesRef, where("dismissed", "!=", true));
        return onSnapshot(q, (snapshot) => {
            const recommendations: StudyRecommendation[] = snapshot.docs.map((doc) => ({
                nctId: doc.data().nctId,
                title: doc.data().title,
                searchId: doc.data().searchId,
                uid: doc.data().uid,
                runId: doc.data().runId,
            }));
            console.log("Recommendations");
            setRecommendedStudies(recommendations);
            setIsLoading(false);
        });
    }, [uid, shouldUseContext]);

    if (shouldUseContext && context) {
        return { recommendedStudies: context.recommendedStudies, isLoading: context.recommendedStudiesLoading };
    }

    return { recommendedStudies, isLoading };
}

export { useAvailableSurveys } from "./useAvailableSurveys";
export type { PendingSurvey } from "./useAvailableSurveys";
